import React, { useState, useEffect, useRef } from 'react';
import { IonButton, IonIcon, IonSpinner } from '@ionic/react';
import { closeOutline, locateOutline } from 'ionicons/icons';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from '../hooks/useTranslation';

// Fix default marker icon issue with webpack/react
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface LocationPickerModalProps {
  isOpen: boolean;
  lat: number;
  lng: number;
  onConfirm: (lat: number, lng: number) => void;
  onClose: () => void;
}

const MapClickHandler: React.FC<{ onLocationSelect: (lat: number, lng: number) => void }> = ({ onLocationSelect }) => {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// Componente para re-centrar el mapa y corregir tiles al abrir
const MapController: React.FC<{ lat: number; lng: number; shouldRecenter: boolean }> = ({ lat, lng, shouldRecenter }) => {
  const map = useMap();

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        map.invalidateSize();
      } catch (e) { /* map not ready */ }
    }, 500);
    return () => clearTimeout(timer);
  }, [map]);

  useEffect(() => {
    if (shouldRecenter) {
      map.setView([lat, lng], map.getZoom());
    }
  }, [lat, lng, shouldRecenter, map]);

  return null;
};

const LocationPickerModal: React.FC<LocationPickerModalProps> = ({ isOpen, lat, lng, onConfirm, onClose }) => {
  const [selectedLat, setSelectedLat] = useState(lat);
  const [selectedLng, setSelectedLng] = useState(lng);
  const [locating, setLocating] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      setSelectedLat(lat);
      setSelectedLng(lng);
      // Forzar re-montar el mapa cada vez que se abre
      setMapKey(prev => prev + 1);
    }
  }, [isOpen, lat, lng]);

  const handleLocationSelect = (newLat: number, newLng: number) => {
    setSelectedLat(newLat);
    setSelectedLng(newLng);
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setSelectedLat(latitude);
        setSelectedLng(longitude);
        setLocating(false);
      },
      () => {
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="location-modal-overlay" onClick={onClose}>
      <div className="location-modal" onClick={(e) => e.stopPropagation()}>
        <div className="location-modal-header">
          <h2>{t('location_picker.title')}</h2>
          <button className="location-modal-close" onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </button>
        </div>

        <div className="location-modal-map">
          <MapContainer
            key={mapKey}
            center={[selectedLat, selectedLng]}
            zoom={14}
            style={{ width: '100%', height: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[selectedLat, selectedLng]} />
            <MapClickHandler onLocationSelect={handleLocationSelect} />
            <MapController lat={selectedLat} lng={selectedLng} shouldRecenter={locating} />
          </MapContainer>
        </div>

        <div className="location-modal-info">
          <p className="location-coords">
            Lat: {selectedLat.toFixed(6)}, Lng: {selectedLng.toFixed(6)}
          </p>
          <p className="location-hint">{t('location_picker.hint')}</p>
        </div>

        <div className="location-modal-actions">
          <IonButton
            fill="outline"
            size="small"
            onClick={handleUseMyLocation}
            disabled={locating}
          >
            {locating ? <IonSpinner name="crescent" /> : <IonIcon icon={locateOutline} slot="start" />}
            {t('location_picker.my_location')}
          </IonButton>
          <IonButton
            color="primary"
            onClick={() => onConfirm(selectedLat, selectedLng)}
          >
            {t('common.confirm')}
          </IonButton>
        </div>
      </div>
    </div>
  );
};

export default LocationPickerModal;
